import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePwa } from "@/lib/pwa";
import { Card, Btn, Badge } from "../AdminUI";
import { RefreshCw, Trash2, CheckCircle2, XCircle, Download } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, ok, value }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="flex items-center gap-2 text-sm font-semibold text-[var(--navy)]">
      {value ?? (ok ? <><CheckCircle2 className="w-4 h-4 text-green-600" /> Active</> : <><XCircle className="w-4 h-4 text-gray-400" /> Inactive</>)}
    </span>
  </div>
);

export default function PwaStatus() {
  const pwa = usePwa();
  const [manifestOk, setManifestOk] = useState(null);
  const [push, setPush] = useState(null);

  useEffect(() => {
    fetch("/manifest.json").then((r) => setManifestOk(r.ok)).catch(() => setManifestOk(false));
    api.get("/admin/integrations").then((r) => setPush(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">PWA Status</h1>
        {pwa?.canInstall && <Btn variant="blue" onClick={pwa.install}><Download className="w-4 h-4" /> Install Veterans PWC</Btn>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Application">
          <Row label="Manifest" ok={manifestOk} value={manifestOk === null ? "Checking…" : undefined} />
          <Row label="Service Worker" ok={pwa?.swReady} />
          <Row label="Current Version" value={`v${pwa?.version}`} />
          <Row label="Latest Deployed Version" value={`v${pwa?.version}`} />
          <Row label="Installability" value={pwa?.installed ? "Installed" : pwa?.canInstall ? "Installable" : "Prompt not available"} />
          <Row label="Offline Cache" ok={pwa?.swReady} />
          <Row label="Update Available" value={pwa?.updateReady ? "Yes — refresh to apply" : "Up to date"} />
          <Row label="Last SW Update" value={pwa?.lastUpdate ? new Date(pwa.lastUpdate).toLocaleString() : "—"} />
        </Card>

        <Card title="Push Notifications">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 mb-3">
            Push notifications are prepared but inactive. No permission is requested and no notifications are sent until a provider is connected and the kill switch is disabled.
          </div>
          <Row label="Push Enabled" ok={push?.push_enabled} />
          <Row label="Provider Connected" ok={push?.push_provider_connected} />
          <Row label="Kill Switch" value={push?.push_kill_switch === false ? "Off" : "Engaged"} />
        </Card>
      </div>

      <Card title="Actions">
        <div className="flex flex-wrap gap-3">
          <Btn variant="ghost" onClick={async () => { await pwa.checkForUpdate(); toast.success("Checked for updates"); }} data-testid="check-update">
            <RefreshCw className="w-4 h-4" /> Check for Update
          </Btn>
          {pwa?.updateReady && <Btn variant="blue" onClick={pwa.applyUpdate}>Apply Update Now</Btn>}
          <Btn variant="danger" onClick={async () => { await pwa.clearPublicCache(); toast.success("Public cache cleared"); }} data-testid="clear-cache">
            <Trash2 className="w-4 h-4" /> Clear Public Cache
          </Btn>
        </div>
        <p className="text-xs text-gray-400 mt-3">Only public assets are cached (app shell, CSS, JS, logo, icons, fonts, public images). Admin data, leads, private photos, transcripts, financing records, and credentials are never cached.</p>
      </Card>
    </div>
  );
}
